/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import {
  Autocomplete,
  AutocompleteProps,
  CircularProgress,
  InputAdornment,
  TextField,
  Paper,
  Box,
  Divider,
  Stack,
  useTheme,
  styled,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Button, { ButtonType } from '../../../../Shared/Components/Common/Buttons/Button';
import { Icons } from '../../../../Shared/Constants/Icons';
import { StyledPagination } from '../../../../Shared/Components/Common/CommonStyle/Pagination.Style';
import { itemsPerPage } from '../../../../Shared/Utilities/utils';

interface GenericAutocompleteWithAddProps<T> extends Partial<AutocompleteProps<T, false, false, false>> {
  options: T[];
  getOptionLabel: (option: T) => string;
  value: T | null;
  onChange: (event: React.SyntheticEvent, newValue: T | null) => void;
  label: React.ReactNode;
  loading?: boolean;
  showAddButton?: boolean;
  onAddClick?: () => void;
  addButtonLabel?: string;
  textFieldSx?: object;
  renderOption?: AutocompleteProps<T, false, false, false>['renderOption'];
  searchString?: string;
  setSearchString?: (value: string) => void;
  totalCounts?: number;
  currentPages?: number;
  handlePaginationChanges?: (event: React.ChangeEvent<unknown>, page: number) => void;
}

const CustomListbox = styled('ul')({
  borderRadius: '16px',
  padding: 0,
  margin: 0,
  position: 'relative',
});

const GenericAutocompleteWithAdd = <T,>({
  options,
  getOptionLabel,
  renderOption,
  value,
  onChange,
  label,
  loading = false,
  showAddButton = false,
  onAddClick,
  addButtonLabel = '',
  textFieldSx,
  searchString = '',
  setSearchString,
  totalCounts = 0,
  currentPages = 1,
  handlePaginationChanges,
  ...rest
}: GenericAutocompleteWithAddProps<T>): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('altTexts');

  const CustomPaper = (props: any) => (
    <Paper {...props} sx={{ opacity: loading ? 0.75 : 1 }}>
      {props.children}
      {showAddButton && (
        <>
          <Divider />
          <Box
            sx={{
              padding: '0.75rem',
              textAlign: 'center',
              backgroundColor: theme.palette.common.white,
            }}>
            <Button
              width="100%"
              icon={<img src={Icons.PlusWhiteIcon} alt={t('addIcon')} />}
              type={ButtonType.Primary}
              label={addButtonLabel}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                onAddClick?.();
              }}
            />
          </Box>
        </>
      )}
    </Paper>
  );

  return (
    <Autocomplete
      options={options}
      getOptionLabel={getOptionLabel}
      value={value}
      onChange={onChange}
      popupIcon={null}
      loading={loading}
      clearOnBlur={false}
      PaperComponent={CustomPaper}
      renderOption={renderOption}
      loadingText={
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
          <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
        </Box>
      }
      {...(setSearchString
        ? {
            inputValue: searchString,
            onInputChange: (_event, newInputValue) => {
              setSearchString(newInputValue);
            },
          }
        : {})}
      ListboxProps={{
        sx: {
          maxHeight: 600,
          padding: 0,
          margin: 0,
          borderRadius: '1rem',
          overflowY: 'auto',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        },
      }}
      componentsProps={{
        paper: {
          sx: {
            borderRadius: '1rem',
            overflow: 'hidden',
          },
        },
      }}
      ListboxComponent={(props) => (
        <CustomListbox {...props}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
              }}>
              <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
            </Box>
          )}
          {props.children}
          {totalCounts > itemsPerPage && handlePaginationChanges && (
            <Stack spacing={2} justifyContent="center" sx={{ marginTop: '1rem', padding: '0.5rem' }}>
              <StyledPagination
                count={Math.ceil(totalCounts / itemsPerPage)}
                page={currentPages}
                onChange={handlePaginationChanges}
                color="primary"
              />
            </Stack>
          )}
        </CustomListbox>
      )}
      {...rest}
      renderInput={(params) => {
        const hasValue = !!params.inputProps.value;
        return (
          <TextField
            {...params}
            sx={textFieldSx}
            label={label}
            InputProps={{
              ...params.InputProps,
              startAdornment: hasValue ? (
                <InputAdornment position="start">
                  <img src={Icons.SearchIcon} alt={t('search')} style={{ cursor: 'pointer' }} />
                </InputAdornment>
              ) : null,
              endAdornment: (
                <>
                  <Box
                    sx={{
                      display: 'contents',
                      '& .MuiAutocomplete-endAdornment': {
                        display: 'contents',
                      },
                      '& .MuiCircularProgress-root': {
                        display: loading ? 'none' : 'inherit',
                      },
                    }}>
                    {params.InputProps.endAdornment}
                  </Box>
                  {!hasValue && <img src={Icons.SearchIcon} alt={t('search')} style={{ cursor: 'pointer' }} />}
                </>
              ),
            }}
          />
        );
      }}
    />
  );
};

export default GenericAutocompleteWithAdd;
